import React, { ChangeEvent, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import TablePagination from '@mui/material/TablePagination';
import LinearProgress from '@mui/material/LinearProgress';

import { Deed as DeedComponent } from 'retired/RadioFilteredList/Deed';
import { selectOrganization, selectUser, selectUserLocations } from 'entities/user/selectors';
import Deed from 'entities/deed/model';
import { selectCauses } from 'entities/cause/selectors';
import getDeedTypeForDisplay from 'utils/getDeedTypeForDisplay';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import { showErrorAction } from 'containers/Notification/actions';
import { ActionResults } from 'containers/DeedsPage/ActionResults';
import {
  useActionsWithPaginationQuery,
  useSearchDeedsQuery,
  useDeedsStatsQuery,
} from 'generated/graphql';

import FilterOptions from './FilterOptions';
import SourceSelector, { SourceSelectorOption } from './SourceSelector';
import Banner from './Banner';
import Tabs from './Tabs';
import SearchInput from './SearchInput';
import ExportLink from './ExportLink';

const DEFAULT_LIMIT = 10;
const ACTIONS_PER_PAGE = 50;

const getDeedTypeFromRouteParam = (type: string) => {
  switch (type) {
    case 'campaigns':
      return 'Campaign';

    case 'projects':
      return 'Project';

    case 'volunteer':
      return 'Event';

    case 'events':
      return 'BaseEvent';

    case 'actions':
      return 'Action';

    default:
      return 'Event';
  }
};

const DeedsPageV2 = () => {
  const user = useSelector(selectUser);
  const causes = useSelector(selectCauses);
  const organization = useSelector(selectOrganization);
  const locations = useSelector(selectUserLocations);

  const dispatch = useDispatch();

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [filterOption, setFilterOption] = useState('all');
  const [sources, setSources] = useState<SourceSelectorOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const { type: routeMatchType } = useParams();
  const deedType = getDeedTypeFromRouteParam(routeMatchType);
  const deedTypeForDisplay = getDeedTypeForDisplay(deedType);

  const isCompany = !!organization?.isCompany();
  const hasSiblingSubsidiaries = !!organization?.siblingSubsidiaries?.length;
  const isActionTabActive = deedType === 'Action';
  const isActionsFeatureEnabled = user?.hasFeature(Features.ACTIONS);
  const hasActionsManagePrivilege = user?.hasPrivilege(Privileges.ACTIONS_MANAGE);

  const resetPagination = useCallback(() => {
    if (page !== 0) {
      setPage(0);
    }
  }, [page]);

  useEffect(() => {
    setFilterOption('all');
    resetPagination();
  }, [routeMatchType]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeSearchTerm = (value: string) => {
    setSearchTerm(value);
    resetPagination();
  };

  const deedsFiltersFromSources = {
    submitter: sources
      .filter((source) => source.type === 'submitter')
      .map((source) => source.value),
    provider: sources.filter((source) => source.type === 'provider').map((source) => source.value),
    subsidiary: sources
      .filter((source) => source.type === 'subsidiary')
      .map((source) => source.value),
  };

  const { loading, data } = useSearchDeedsQuery({
    variables: {
      filterOption,
      searchTerm,
      deedType,
      ...deedsFiltersFromSources,
      offset: page * limit,
      limit,
    },
    fetchPolicy: 'no-cache',
    skip: deedType === 'Action',
  });

  const { data: statsData } = useDeedsStatsQuery({
    variables: {
      searchTerm,
      deedType,
      ...deedsFiltersFromSources,
    },
    fetchPolicy: 'no-cache',
    skip: deedType === 'Action',
  });

  const deeds = data?.searchDeeds?.deeds.map((deed) => new Deed(deed)) || [];
  const total = data?.searchDeeds?.total || 0;
  const sharedDeedsTotal = statsData?.deedsStats?.sharedDeedsTotal || 0;
  const pendingDeedsTotal = statsData?.deedsStats?.pendingReviewDeedsTotal || 0;
  const draftDeedsTotal = statsData?.deedsStats?.draftDeedsTotal || 0;

  const [actionsPage, setActionsPage] = useState(0);
  const [actionsLimit, setActionsLimit] = useState(ACTIONS_PER_PAGE);

  const handleActionsChangePage = (_event: unknown, newPage: number) => {
    setActionsPage(newPage);
  };

  const handleActionsChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setActionsLimit(parseInt(event.target.value, 10));
    setActionsPage(0);
  };

  const {
    data: actionsData,
    error: actionsError,
    loading: actionsLoading,
  } = useActionsWithPaginationQuery({
    variables: {
      skip: actionsPage * actionsLimit,
      take: actionsLimit,
    },
    skip: !isActionsFeatureEnabled || !hasActionsManagePrivilege,
  });

  if (actionsError) {
    dispatch(showErrorAction('An error occurred during loading your actions, please try again.'));
  }

  if (!organization) {
    // @TODO-DP: add loader
    return null;
  }

  const showLoader =
    !user || (isActionTabActive && actionsLoading) || (!isActionTabActive && loading);

  const actionsTotal = actionsData?.actionsWithPagination?.metadata?.totalItems ?? 0;
  const actionItems = actionsData?.actionsWithPagination?.items ?? [];
  const content = isActionTabActive ? (
    <>
      <ActionResults
        actions={actionItems || []}
        isLoading={actionsLoading}
        organization={organization}
        actionsTotal={actionsTotal}
        actionsPage={actionsPage}
        actionsLimit={actionsLimit}
        handlePageChange={handleActionsChangePage}
        handleRowsPerPageChange={handleActionsChangeRowsPerPage}
      />
    </>
  ) : (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <FilterOptions
          setFilterOption={setFilterOption}
          filterOption={filterOption}
          deedType={deedType}
          resetPagination={resetPagination}
          hasSiblingSubsidiaries={hasSiblingSubsidiaries}
          sharedDeedsTotal={sharedDeedsTotal}
          pendingDeedsTotal={pendingDeedsTotal}
          draftDeedsTotal={draftDeedsTotal}
        />

        <div style={{ display: 'flex', marginBottom: '8px' }}>
          <SearchInput handleSearchInput={handleChangeSearchTerm} />
          <SourceSelector organization={organization} sources={sources} onChange={setSources} />
        </div>
      </div>

      <div>
        {deeds.map((deed) => (
          <DeedComponent
            key={deed.id}
            deed={deed}
            causes={causes}
            isCompany={isCompany}
            user={user}
            locations={locations}
          />
        ))}
        <TablePagination
          style={{ marginBottom: '60px' }}
          component="div"
          count={total}
          page={page}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </>
  );

  return (
    <>
      <Helmet title={[deedTypeForDisplay, 'Deeds'].filter(Boolean).join(' ')} />

      <Banner deedType={deedType} />

      {showLoader ? <LinearProgress /> : <div style={{ paddingBottom: '4px' }} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Tabs />

        {deeds.length > 0 && (
          <ExportLink
            deedType={deedType}
            organizationId={organization.id}
            deedTypeForDisplay={deedTypeForDisplay}
            searchTerm={searchTerm}
          />
        )}
      </div>

      {content}
    </>
  );
};

export default DeedsPageV2;
